import ApiService from "@/api/ApiService";
import { Sync } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { crewSyncConfig, crewSyncBlockReason } from "./crewSyncState";
import type { SettingsActionProps } from "./definitions";

type Issue = {
  jobId: number; date: string; projectName: string; reason: string; message: string;
  name: string | null; bookingId?: number; restorable: boolean;
};
type Result = {
  jobs: number; processedJobs: number; created: number; removed: number; restored: number;
  reassigned: number; recordersChanged: number; issues: Issue[]; jobIds: number[]; projectIds: number[];
};
type ApiError = { data?: { error?: string } };

export default function CrewSyncAction({ id, savedObject, dirty, disabled, onPendingChange }: SettingsActionProps) {
  const expected = savedObject ? crewSyncConfig(savedObject) : null;
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const [result, setResult] = useState<Result | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const mutation = useMutation<Result, ApiError, number[]>({
    mutationFn: (restoreBookingIds) => ApiService.updateCrew()({ id, expected, restoreBookingIds }),
    onMutate: () => { onPendingChange(true); setNotice(null); setResult(null); setSelected([]); },
    onSettled: () => onPendingChange(false),
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ["getCalendar"] });
      queryClient.invalidateQueries({ queryKey: ["getOpenRequests"] });
      for (const projectId of data.projectIds) {
        queryClient.invalidateQueries({ queryKey: ["getJobStates", String(projectId)] });
      }
      for (const jobId of data.jobIds) {
        queryClient.invalidateQueries({ queryKey: ["getProjectJob", String(jobId)] });
        queryClient.invalidateQueries({ queryKey: ["getJobApp", String(jobId)] });
      }
    },
  });

  // A result/selection belongs to the saved configuration, not a later edit or crew.
  const configKey = JSON.stringify(expected);
  const { reset } = mutation;
  useEffect(() => {
    setOpen(false); setResult(null); setSelected([]); setNotice(null); reset();
  }, [id, configKey, dirty, reset]);

  function run(restoreIds: number[] = []) {
    setOpen(true);
    mutation.reset();
    const reason = crewSyncBlockReason(dirty, expected !== null);
    if (reason) { setNotice(reason); return; }
    mutation.mutate(restoreIds);
  }

  return (
    <>
      <LoadingButton onClick={() => run()} loading={mutation.isPending} disabled={disabled}
        variant="text" startIcon={<Sync />}>
        Kolonne glattziehen
      </LoadingButton>
      <Dialog open={open} onClose={() => { if (!mutation.isPending) setOpen(false); }}
        fullWidth maxWidth="md" aria-labelledby="crew-sync-title">
        <DialogTitle id="crew-sync-title">Kolonne glattziehen</DialogTitle>
        <DialogContent>
          {mutation.isPending && <Typography role="status">Kolonnenbuchungen werden abgeglichen …</Typography>}
          {notice && <Alert severity="warning">{notice}</Alert>}
          {mutation.isError && <Alert severity="error">
            {mutation.error?.data?.error ?? "Der Abgleich ist fehlgeschlagen. Bitte erneut versuchen."}
          </Alert>}
          {result && <Box sx={{ mt: 1 }}>
            <Alert severity={result.issues.length ? "warning" : "success"}>
              {result.jobs === 0 ? "Keine bestehenden Kolonnenplanungen nach heute gefunden."
                : `${result.processedJobs} von ${result.jobs} zukünftigen Jobs abgeglichen. ${result.created} Buchungen ergänzt, ${result.removed} entfernt, ${result.restored} wiederhergestellt, ${result.reassigned} neu zugeordnet, ${result.recordersChanged} Erfasser geändert.`}
              {result.issues.length > 0 && " Der Abgleich enthält Ausnahmen; siehe unten."}
            </Alert>
            {result.issues.map((issue, index) => <Box key={`${issue.jobId}-${issue.reason}-${issue.bookingId ?? index}`} sx={{ mt: 1 }}>
              <Typography variant="body2">
                {issue.date} · {issue.projectName}{issue.name ? ` · ${issue.name}` : ""}: {issue.message}
              </Typography>
              {issue.restorable && issue.bookingId !== undefined && <FormControlLabel
                control={<Checkbox disabled={disabled || mutation.isPending || dirty}
                  checked={selected.includes(issue.bookingId)} onChange={(_, checked) => {
                    const bookingId = issue.bookingId!;
                    setSelected((ids) => checked ? [...ids, bookingId] : ids.filter((entry) => entry !== bookingId));
                  }} />}
                label="Diese gelöschte Buchung wiederherstellen" />}
            </Box>)}
            {result.issues.some((issue) => issue.restorable) && <>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Gelöschte Buchungen bleiben erhalten, bis Sie sie ausdrücklich auswählen.
                Bei einem Konflikt beim Polier bleibt der betreffende Job unverändert.
              </Typography>
              <LoadingButton onClick={() => run(selected)} loading={mutation.isPending}
                disabled={disabled || dirty || selected.length === 0}>
                Ausgewählte wiederherstellen und erneut abgleichen
              </LoadingButton>
            </>}
          </Box>}
        </DialogContent>
        <DialogActions>
          {mutation.isError && <LoadingButton onClick={() => run(mutation.variables ?? [])} disabled={disabled}>Erneut versuchen</LoadingButton>}
          <Button onClick={() => setOpen(false)} disabled={mutation.isPending}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
